import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Link, Card, CardContent, Avatar } from '@mui/material';
import Grid from '@mui/material/Grid';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import { FlagsContainer, FrenchFlag, EnglishFlag, GermanFlag } from '../../components/flags/Flags';
import CamilleDouayPhoto from '../../images/contact_images/CamilleDouay.png';
import PaulFliegansPhoto from '../../images/contact_images/PaulFliegans.png';
import MelanieArcicasaPhoto from '../../images/contact_images/MelanieArcicasa.png';
import MaximeDegrellePhoto from '../../images/contact_images/MaximeDegrelle.png';

export const Pratique = () => {
   const { t } = useTranslation();
   return (
      <Box sx={{ padding: '2rem' }}>
         <Box sx={{ marginBottom: 6, marginTop: 3 }} id="venir">
            <Typography variant="h4" gutterBottom>
               {t('useful.coming.title')}
            </Typography>
            <Typography paragraph>{t('useful.coming.content1')}</Typography>
            <Typography paragraph>{t('useful.coming.content2')}</Typography>
            <Typography paragraph>{t('useful.coming.content3')}</Typography>
            <Link href="https://www.bahn.de/" target="_blank" rel="noopener noreferrer">
               https://www.bahn.de/
            </Link>
         </Box>

         <Box sx={{ marginBottom: 6 }} id="logement">
            <Typography variant="h4" gutterBottom>
               {t('useful.accommodation.title')}
            </Typography>
            <Typography paragraph>{t('useful.accommodation.content1')}</Typography>
            <Typography paragraph>{t('useful.accommodation.content2')}</Typography>
            <Typography paragraph>{t('useful.accommodation.content3')}</Typography>
         </Box>

         <Box sx={{ marginBottom: 6 }} id="deplacement">
            <Typography variant="h4" gutterBottom>
               {t('useful.transport.title')}
            </Typography>
            <Typography paragraph>{t('useful.transport.content1')}</Typography>
            <Typography paragraph>{t('useful.transport.content2')}</Typography>
            <Link
               href="https://www.polsoz.fu-berlin.de/v/dgpuk2012/anreise/S_UAusschnitt_1112_2011_1_.pdf"
               target="_blank"
               rel="noopener noreferrer"
            >
               Télécharger le plan S+U Bahn (PDF)
            </Link>
         </Box>

         <Box id="contact">
            <Typography variant="h4" gutterBottom>
               {t('useful.contact.title')}
            </Typography>
            <Typography paragraph sx={{ marginBottom: 3 }}>
               {t('useful.contact.content1')}
            </Typography>

            <Grid container spacing={4}>
               {/* Camille */}
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                     sx={{
                        textAlign: 'center',
                        padding: 2,
                        height: '100%',
                        boxShadow: 3,
                        '&:hover': { boxShadow: 6 },
                        position: 'relative',
                        overflow: 'visible',
                        marginTop: 2,
                        marginBottom: 2,
                     }}
                  >
                     {/* Language Ribbons */}
                     <FlagsContainer>
                        <FrenchFlag />
                        <EnglishFlag />
                     </FlagsContainer>
                     <Avatar
                        src={CamilleDouayPhoto}
                        alt="Camille Douay"
                        sx={{
                           width: 100,
                           height: 100,
                           margin: '0 auto 1rem',
                           bgcolor: '#BFA2DB',
                        }}
                     >
                        CD
                     </Avatar>
                     <CardContent>
                        <Typography variant="h6" gutterBottom>
                           Camille Douay
                        </Typography>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              mb: 1,
                           }}
                        >
                           <PhoneIcon fontSize="small" color="action" />
                           <Typography variant="body2" color="text.secondary">
                              +33 6 28 02 64 61
                           </Typography>
                        </Box>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                           }}
                        >
                           <EmailIcon fontSize="small" color="action" />
                           <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word' }}
                           >
                              camille.douay5@gmail.com
                           </Typography>
                        </Box>
                     </CardContent>
                  </Card>
               </Grid>

               {/* Paul */}
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                     sx={{
                        textAlign: 'center',
                        padding: 2,
                        height: '100%',
                        boxShadow: 3,
                        '&:hover': { boxShadow: 6 },
                        position: 'relative',
                        overflow: 'visible',
                        marginTop: 2,
                        marginBottom: 2,
                     }}
                  >
                     {/* Language Ribbons */}
                     <FlagsContainer>
                        <FrenchFlag />
                        <EnglishFlag />
                        <GermanFlag />
                     </FlagsContainer>
                     <Avatar
                        src={PaulFliegansPhoto}
                        alt="Paul Fliegans"
                        sx={{
                           width: 100,
                           height: 100,
                           margin: '0 auto 1rem',
                           bgcolor: '#A98DC6',
                        }}
                     >
                        PF
                     </Avatar>
                     <CardContent>
                        <Typography variant="h6" gutterBottom>
                           Paul Fliegans
                        </Typography>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              mb: 1,
                           }}
                        >
                           <PhoneIcon fontSize="small" color="action" />
                           <Typography variant="body2" color="text.secondary">
                              +49 176 20222785
                           </Typography>
                        </Box>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                           }}
                        >
                           <EmailIcon fontSize="small" color="action" />
                           <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word' }}
                           >
                              paul.fliegans@gmail.com
                           </Typography>
                        </Box>
                     </CardContent>
                  </Card>
               </Grid>

               {/* Mel */}
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                     sx={{
                        textAlign: 'center',
                        padding: 2,
                        height: '100%',
                        boxShadow: 3,
                        '&:hover': { boxShadow: 6 },
                        position: 'relative',
                        overflow: 'visible',
                        marginTop: 2,
                        marginBottom: 2,
                     }}
                  >
                     {/* Language Ribbons */}
                     <FlagsContainer>
                        <FrenchFlag />
                        <EnglishFlag />
                     </FlagsContainer>
                     <Avatar
                        src={MelanieArcicasaPhoto}
                        alt="Mélanie Arcicasa"
                        sx={{
                           width: 100,
                           height: 100,
                           margin: '0 auto 1rem',
                           bgcolor: '#C8E6C9',
                        }}
                     >
                        MA
                     </Avatar>
                     <CardContent>
                        <Typography variant="h6" gutterBottom>
                           Mélanie Arcicasa
                        </Typography>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                           }}
                        >
                           <EmailIcon fontSize="small" color="action" />
                           <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word' }}
                           >
                              melanie.arcicasa@gmail.com
                           </Typography>
                        </Box>
                     </CardContent>
                  </Card>
               </Grid>

               {/* Max */}
               <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card
                     sx={{
                        textAlign: 'center',
                        padding: 2,
                        height: '100%',
                        boxShadow: 3,
                        '&:hover': { boxShadow: 6 },
                        position: 'relative',
                        overflow: 'visible',
                        marginTop: 2,
                        marginBottom: 2,
                     }}
                  >
                     {/* Language Ribbons */}
                     <FlagsContainer>
                        <FrenchFlag />
                     </FlagsContainer>
                     <Avatar
                        src={MaximeDegrellePhoto}
                        alt="Maxime Degrelle"
                        sx={{
                           width: 100,
                           height: 100,
                           margin: '0 auto 1rem',
                           bgcolor: '#E6DAF0',
                        }}
                     >
                        MD
                     </Avatar>
                     <CardContent>
                        <Typography variant="h6" gutterBottom>
                           Maxime Degrelle
                        </Typography>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                              mb: 1,
                           }}
                        >
                           <PhoneIcon fontSize="small" color="action" />
                           <Typography variant="body2" color="text.secondary">
                              +33 6 33 24 84 21
                           </Typography>
                        </Box>
                        <Box
                           sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 1,
                           }}
                        >
                           <EmailIcon fontSize="small" color="action" />
                           <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ wordBreak: 'break-word' }}
                           >
                              maxime.degrelle95@hotmail.fr
                           </Typography>
                        </Box>
                     </CardContent>
                  </Card>
               </Grid>
            </Grid>
         </Box>
      </Box>
   );
};
